import React, { useRef } from "react";
import { Typography, Divider, Select } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { ButtonItem, InputItem, TextAreaItem } from "../../../../Components/Items";
import { WA } from "../theme";
import { TIPOS_INTERACTIVO, payloadVacio } from "./Helpers/interactivosHelpers";
import BarraFormato from "./BarraFormato";

const { Text } = Typography;

let _ciSeq = 0;

const ConstructorInteractivo = ({ tipo, setTipo, payload, setPayload, bloquearTipo = false }) => {
  const idCuerpo = useRef(`wa-ci-cuerpo-${++_ciSeq}`).current;
  const cambiarTipo = (nuevo) => {
    setTipo(nuevo);
    setPayload(payloadVacio(nuevo));
  };

  const setCampo = (ruta, valor) =>
    setPayload((prev) => ({ ...prev, [ruta]: { ...prev[ruta], text: valor } }));

  const setBoton = (i, valor) =>
    setPayload((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b, idx) => (idx === i ? { ...b, title: valor } : b)),
    }));
  const agregarBoton = () =>
    setPayload((prev) => ({ ...prev, buttons: [...prev.buttons, { title: "" }] }));
  const quitarBoton = (i) =>
    setPayload((prev) => ({ ...prev, buttons: prev.buttons.filter((_, idx) => idx !== i) }));

  const setFila = (si, ri, campo, valor) =>
    setPayload((prev) => ({
      ...prev,
      sections: prev.sections.map((s, sIdx) =>
        sIdx !== si ? s : { ...s, rows: s.rows.map((r, rIdx) => (rIdx === ri ? { ...r, [campo]: valor } : r)) },
      ),
    }));
  const agregarFila = (si) =>
    setPayload((prev) => ({
      ...prev,
      sections: prev.sections.map((s, sIdx) =>
        sIdx !== si ? s : { ...s, rows: [...s.rows, { title: "", description: "" }] },
      ),
    }));
  const quitarFila = (si, ri) =>
    setPayload((prev) => ({
      ...prev,
      sections: prev.sections.map((s, sIdx) =>
        sIdx !== si ? s : { ...s, rows: s.rows.filter((_, rIdx) => rIdx !== ri) },
      ),
    }));

  const totalFilas = (payload.sections || []).reduce((acc, s) => acc + (s.rows?.length || 0), 0);
  const tituloSeccion = { fontWeight: 600, margin: "12px 0 4px", color: WA.textPrimary };

  return (
    <>
      <div style={tituloSeccion}>Tipo</div>
      <Select
        value={tipo}
        onChange={cambiarTipo}
        disabled={bloquearTipo}
        style={{ width: "100%" }}
        getPopupContainer={(t) => t.parentElement}
        options={TIPOS_INTERACTIVO.map((t) => ({ value: t.value, label: t.label }))}
      />
      {bloquearTipo && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          El tipo no se puede cambiar al editar.
        </Text>
      )}

      <Divider style={{ margin: "16px 0 8px" }} />

      <div style={tituloSeccion}>Encabezado (opcional)</div>
      <InputItem
        placeholder="Título breve"
        value={payload.header?.text || ""}
        onChange={(e) => setCampo("header", e.target.value)}
        maxLength={60}
      />

      <div style={tituloSeccion}>Cuerpo *</div>
      <BarraFormato targetId={idCuerpo} value={payload.body?.text || ""} onChange={(v) => setCampo("body", v)} />
      <TextAreaItem
        id={idCuerpo}
        placeholder="Texto principal del mensaje"
        value={payload.body?.text || ""}
        onChange={(e) => setCampo("body", e.target.value)}
        rows={3}
        maxLength={1024}
      />

      <div style={tituloSeccion}>Pie (opcional)</div>
      <InputItem
        placeholder="Nota al pie"
        value={payload.footer?.text || ""}
        onChange={(e) => setCampo("footer", e.target.value)}
        maxLength={60}
      />

      {tipo === "buttons" && (
        <>
          <div style={tituloSeccion}>Botones (máx. 3)</div>
          {payload.buttons.map((b, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <InputItem
                placeholder={`Botón ${i + 1}`}
                value={b.title}
                onChange={(e) => setBoton(i, e.target.value)}
                maxLength={20}
              />
              {payload.buttons.length > 1 && (
                <ButtonItem rojo icon={<DeleteOutlined />} onClick={() => quitarBoton(i)} />
              )}
            </div>
          ))}
          {payload.buttons.length < 3 && (
            <ButtonItem lima icon={<PlusOutlined />} onClick={agregarBoton}>
              Agregar botón
            </ButtonItem>
          )}
        </>
      )}

      {tipo === "list" && (
        <>
          <div style={tituloSeccion}>Texto del botón de la lista *</div>
          <InputItem
            placeholder="Ver opciones"
            value={payload.button || ""}
            onChange={(e) => setPayload((prev) => ({ ...prev, button: e.target.value }))}
            maxLength={20}
          />
          <div style={tituloSeccion}>Opciones (máx. 10 en total — actual: {totalFilas})</div>
          {payload.sections.map((s, si) => (
            <div key={si} style={{ border: `1px solid ${WA.border}`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
              {s.rows.map((r, ri) => (
                <div key={ri} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <InputItem
                      placeholder={`Opción ${ri + 1}`}
                      value={r.title}
                      onChange={(e) => setFila(si, ri, "title", e.target.value)}
                      maxLength={24}
                    />
                    {totalFilas > 1 && (
                      <ButtonItem rojo icon={<DeleteOutlined />} onClick={() => quitarFila(si, ri)} />
                    )}
                  </div>
                  <InputItem
                    placeholder="Descripción (opcional)"
                    value={r.description || ""}
                    onChange={(e) => setFila(si, ri, "description", e.target.value)}
                    maxLength={72}
                    style={{ marginTop: 4 }}
                  />
                </div>
              ))}
              {totalFilas < 10 && (
                <ButtonItem lima icon={<PlusOutlined />} onClick={() => agregarFila(si)}>
                  Agregar opción
                </ButtonItem>
              )}
            </div>
          ))}
        </>
      )}

      {tipo === "cta_url" && (
        <>
          <div style={tituloSeccion}>Texto del botón *</div>
          <InputItem
            placeholder="Ej. Ver más"
            value={payload.display_text || ""}
            onChange={(e) => setPayload((prev) => ({ ...prev, display_text: e.target.value }))}
            maxLength={20}
          />
          <div style={tituloSeccion}>URL *</div>
          <InputItem
            placeholder="https://..."
            value={payload.url || ""}
            onChange={(e) => setPayload((prev) => ({ ...prev, url: e.target.value }))}
          />
        </>
      )}
    </>
  );
};

// Valida el payload construido. Devuelve un mensaje de error o null.
export const validarConstruido = (tipo, payload) => {
  if (!payload.body?.text?.trim()) return "El cuerpo del mensaje es obligatorio.";
  if (tipo === "buttons" && payload.buttons.some((b) => !b.title.trim()))
    return "Cada botón necesita un texto.";
  if (tipo === "list" && (payload.sections || []).some((s) => s.rows.some((r) => !r.title.trim())))
    return "Cada opción de la lista necesita un título.";
  if (tipo === "cta_url" && (!payload.display_text?.trim() || !payload.url?.trim()))
    return "Completa el texto y la URL del botón.";
  return null;
};

export default ConstructorInteractivo;
